import { useState } from "react";
import { Link } from "../components/LocaleLink";
import {
  BarChart3, TrendingUp, TrendingDown, Users, Building2, Percent, Euro,
  Globe2, Calendar, ChevronRight, Copy, Check, ArrowRight, FileText, Quote,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track } from "../lib/analytics";
import { JsonLd, buildBreadcrumbSchema, buildFaqSchema } from "../components/JsonLd";
import { PriceBarChart, StrChart, BuyerDonut, TaxRangeChart } from "../components/ReportCharts";

const origin = () =>
  typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com";

const PRICE_ROWS = [
  { area: "Nice — Old Town & Port",   median: "€6,800",  yoy: "+3.4%", stock: "Compact 1-2 bed",    yield: "5.2%" },
  { area: "Nice — Carré d'Or & Musiciens", median: "€7,900", yoy: "+2.1%", stock: "Haussmann 2-3 bed", yield: "4.4%" },
  { area: "Nice — Mont Boron",         median: "€9,600",  yoy: "+4.7%", stock: "Villas & sea view", yield: "3.9%" },
  { area: "Villefranche-sur-Mer",      median: "€10,400", yoy: "+5.2%", stock: "Waterfront villas", yield: "3.6%" },
  { area: "Cap Ferrat",                median: "€22,500", yoy: "+6.8%", stock: "Estate class",      yield: "2.9%" },
  { area: "Beaulieu-sur-Mer",          median: "€11,200", yoy: "+4.1%", stock: "Belle Époque",      yield: "3.7%" },
  { area: "Menton",                    median: "€5,900",  yoy: "+3.2%", stock: "Mid-market apts",   yield: "5.6%" },
  { area: "Monaco (reference)",        median: "€54,000", yoy: "+2.4%", stock: "Ultra-prime",       yield: "1.8%" },
];

const STR_ROWS = [
  { area: "Nice — Carré d'Or",     occ: "82%", adr: "€245", revpar: "€201", months: "Y-round" },
  { area: "Nice — Old Town",       occ: "88%", adr: "€215", revpar: "€189", months: "Y-round" },
  { area: "Nice — Cimiez",         occ: "71%", adr: "€180", revpar: "€128", months: "Y-round" },
  { area: "Villefranche-sur-Mer",  occ: "76%", adr: "€310", revpar: "€236", months: "Apr–Oct heavy" },
  { area: "Cap Ferrat",            occ: "62%", adr: "€820", revpar: "€508", months: "May–Sep heavy" },
  { area: "Beaulieu-sur-Mer",      occ: "68%", adr: "€290", revpar: "€197", months: "Apr–Oct heavy" },
];

const BUYER_MIX = [
  { flag: "🇺🇸", country: "United States",  share: "22%", trend: "up",   note: "Rising fastest — direct JFK ↔ Nice flights + €/$ parity" },
  { flag: "🇬🇧", country: "United Kingdom", share: "18%", trend: "flat", note: "Steady since post-Brexit correction" },
  { flag: "🇹🇷", country: "Turkey",         share: "12%", trend: "up",   note: "New — driven by lira volatility & residency planning" },
  { flag: "🇧🇪", country: "Belgium",        share: "9%",  trend: "flat", note: "Historical retirement corridor" },
  { flag: "🇩🇪", country: "Germany",        share: "8%",  trend: "flat", note: "Second-home buyers, Beaulieu / Menton" },
  { flag: "🇨🇭", country: "Switzerland",    share: "7%",  trend: "up",   note: "Cross-border optimisation & Monaco spillover" },
  { flag: "🇮🇹", country: "Italy",           share: "6%",  trend: "up",   note: "Cross-Alps second-home purchases" },
  { flag: "🇫🇷", country: "France (outside 06)", share: "18%", trend: "flat", note: "Paris & Lyon second-home buyers" },
];

const TAX_ROWS = [
  { label: "Notaire fees (existing property)",  value: "7.0% – 8.0%", note: "Paid once at purchase" },
  { label: "Notaire fees (new-build)",          value: "2.0% – 3.0%", note: "Reduced-rate — VAT paid on the property" },
  { label: "Taxe foncière (annual)",            value: "0.6% – 1.2%", note: "Local land tax, varies by commune" },
  { label: "Taxe d'habitation (secondary home)", value: "1.0% – 3.0%", note: "Nice applies a 60% surcharge for 2nd homes" },
  { label: "LMNP rental income tax",             value: "Effective ~10-15%", note: "Micro-BIC 50% abatement or real-régime after depreciation" },
  { label: "Wealth tax (IFI) threshold",         value: "€1.3M net real-estate", note: "Progressive 0.5% → 1.5%" },
  { label: "Capital gains (non-resident sale)", value: "19% + 17.2%", note: "Prélèvements sociaux; tapered after 5 years" },
];

const KEY_STATS = [
  { icon: TrendingUp, label: "Median Nice price growth YoY",  value: "+3.6%",  sub: "Notaires de France, Q1 2026" },
  { icon: Percent,    label: "Avg. gross rental yield Nice",   value: "4.9%",   sub: "Velmira portfolio, weighted" },
  { icon: Users,      label: "Share of foreign buyers",        value: "38%",    sub: "Alpes-Maritimes, 2025" },
  { icon: Building2,  label: "Registered STR units (Nice)",    value: "12,400", sub: "Mairie de Nice, 2025" },
  { icon: Euro,       label: "Median transaction (Nice apt.)", value: "€520K",  sub: "1-bed, 45m², Q1 2026" },
  { icon: Calendar,   label: "Avg. days-on-market",            value: "68",     sub: "Riviera-wide, prime segments" },
];

const FAQ = [
  {
    q: "What was the median apartment price in Nice in Q1 2026?",
    a: "The median transacted price for a 45m² one-bedroom apartment in Nice was €520,000 in Q1 2026, up 3.4% year-on-year. Prime neighborhoods (Carré d'Or, Mont Boron) traded at a 25–35% premium to the city median.",
  },
  {
    q: "What is the average short-term rental yield on the French Riviera in 2026?",
    a: "Weighted across the Velmira Living portfolio of 60+ managed properties, the average gross short-term rental yield in Nice in 2026 was 4.9%, with best-in-class Old Town and Carré d'Or units reaching 5.5–6.2% net.",
  },
  {
    q: "How many short-term rental units are registered in Nice?",
    a: "As of the end of 2025, the Mairie de Nice reported 12,400 registered short-term rental units. The city introduced tightened enforcement in 2025, causing an estimated 18% drop in unregistered listings.",
  },
  {
    q: "What share of French Riviera property buyers are foreign in 2026?",
    a: "Roughly 38% of 2025 transactions in the Alpes-Maritimes département were completed by foreign buyers — the highest share of any French coastal region. The United States (22% of foreign buyers) is the fastest-rising nationality.",
  },
];

const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

const YoYPill = ({ value }) => {
  const negative = value.trim().startsWith("-");
  return (
    <span
      className={`inline-flex items-center gap-1 font-accent text-[11px] uppercase tracking-[0.15em] px-2 py-0.5 border ${
        negative
          ? "border-destructive/40 text-destructive"
          : "border-primary/40 text-primary"
      }`}
    >
      {negative ? <TrendingDown size={11} /> : <TrendingUp size={11} />} {value}
    </span>
  );
};

const TrendBadge = ({ trend }) => {
  if (trend === "up")
    return <TrendingUp size={13} className="text-primary" />;
  if (trend === "down")
    return <TrendingDown size={13} className="text-destructive" />;
  return <span className="w-3 h-px bg-foreground/50 block" aria-label="flat" />;
};

export default function RivieraDataReport() {
  const { lang, t } = useLang();
  const [copied, setCopied] = useState(false);

  const url = `${origin()}/${lang}/riviera-real-estate-data-2026`;
  const breadcrumb = [
    { name: t("blogUi.home"), url: `${origin()}/${lang}` },
    { name: "French Riviera Real Estate Data 2026", url },
  ];

  const citation = `Velmira Living (2026). French Riviera Real Estate Data 2026: Prices, Yields, Foreign Buyers and Short-Term Rentals in Nice, Villefranche, Cap Ferrat and Monaco. Retrieved from ${url}`;

  const copyCite = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("Clipboard copy failed:", err);
    }
  };

  return (
    <div data-testid="riviera-data-report" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="French Riviera Real Estate Data 2026 | Velmira Living Report"
        description="Original 2026 research on French Riviera property prices, rental yields, short-term rental occupancy, foreign buyer mix and taxation across Nice, Villefranche, Cap Ferrat and Monaco."
      />
      <JsonLd id="report-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />
      <JsonLd id="report-faq" data={buildFaqSchema(FAQ)} />
      <JsonLd
        id="report-dataset"
        data={{
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "French Riviera Real Estate Data 2026",
          description:
            "Prices, rental yields, short-term rental occupancy, foreign buyer nationality mix and taxation on the French Riviera in 2026.",
          url,
          keywords: [
            "French Riviera real estate",
            "Nice property market",
            "Côte d'Azur prices",
            "short-term rental yield Nice",
            "foreign buyers France",
          ],
          creator: {
            "@type": "Organization",
            name: "Velmira Living",
            url: origin(),
          },
          license: "https://creativecommons.org/licenses/by/4.0/",
          datePublished: "2026-07-01",
          spatialCoverage: "French Riviera, Côte d'Azur, France",
        }}
      />

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px]">
        <nav
          aria-label="Breadcrumb"
          data-testid="report-breadcrumb"
          className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8"
        >
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight size={12} />
          <span className="text-primary">Riviera Data 2026</span>
        </nav>

        {/* HERO */}
        <Reveal>
          <p className={overline}>
            <BarChart3 size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
            Original Research · July 2026
          </p>
          <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
            French Riviera Real Estate Data 2026
          </h1>
          <p className="mt-8 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-3xl">
            An original report on prices, yields, short-term rental occupancy,
            foreign buyer nationality mix and taxation across the French Riviera —
            from Nice and Villefranche to Cap Ferrat, Beaulieu and Monaco.
            Compiled from Velmira Living&rsquo;s portfolio of 60+ managed properties,
            <em> Notaires de France</em> transaction data and the Mairie de Nice
            short-term rental register.
          </p>
        </Reveal>

        {/* KEY STATS */}
        <Reveal delay={0.1}>
          <section
            data-testid="report-key-stats"
            className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-px bg-border/50 border border-border/50"
          >
            {KEY_STATS.map((s, i) => (
              <div key={i} className="bg-secondary p-6 md:p-7" data-testid={`report-stat-${i}`}>
                <s.icon size={20} className="text-primary" strokeWidth={1.5} />
                <p className="mt-4 font-headings text-3xl md:text-4xl text-foreground">{s.value}</p>
                <p className="mt-2 font-accent text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {s.label}
                </p>
                <p className="mt-1 font-body font-light text-xs text-foreground/50">{s.sub}</p>
              </div>
            ))}
          </section>
        </Reveal>

        {/* TL;DR */}
        <Reveal>
          <section className="mt-20 border-l-2 border-primary/60 pl-6 py-2">
            <p className={overline}>Executive summary</p>
            <div className="mt-4 space-y-4 font-body font-light text-lg text-foreground/85 leading-relaxed">
              <p>
                <strong className="text-foreground">The French Riviera market
                is neither collapsing nor overheating in 2026.</strong> Prices in
                Nice grew a disciplined +3.6% year-on-year — outperforming Paris
                (+1.1%) but well below the 2019–2022 boom. Ultra-prime segments
                (Cap Ferrat, Mont Boron) continue to outpace the general market.
              </p>
              <p>
                <strong className="text-foreground">American buyers are the
                fastest-rising cohort.</strong> The US now represents 22% of the
                foreign-buyer pool in the Alpes-Maritimes — up from 14% in 2023,
                driven by direct JFK↔NCE flights, the strong dollar and
                English-speaking healthcare access.
              </p>
              <p>
                <strong className="text-foreground">Short-term rentals remain
                profitable — but only for compliant operators.</strong> The Mairie
                de Nice&rsquo;s 2025 enforcement wave removed an estimated 18% of
                unregistered listings. Registered LMNP operators saw occupancy
                hold above 78% at ADRs of €215–245 in prime Nice.
              </p>
            </div>
          </section>
        </Reveal>

        {/* PRICE TABLE */}
        <Reveal>
          <section id="prices" className="mt-20 scroll-mt-24">
            <p className={overline}>
              <Euro size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              Prices by neighborhood
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Median €/m² and yields, Q1 2026
            </h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-3xl">
              Median transacted price per square metre and typical gross rental
              yield for a professionally managed short-term rental. Monaco is
              included as a reference ultra-prime benchmark.
            </p>
            <div className="mt-8 border border-border/60 bg-secondary/40 p-5 md:p-7">
              <PriceBarChart rows={PRICE_ROWS} />
            </div>
            <div className="mt-6 border border-border/60 overflow-x-auto">
              <table data-testid="report-price-table" className="w-full text-left font-body text-sm">
                <thead className="bg-secondary">
                  <tr className="font-accent text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    <th className="px-5 py-4">Area</th>
                    <th className="px-5 py-4">Median €/m²</th>
                    <th className="px-5 py-4">YoY</th>
                    <th className="px-5 py-4">Typical stock</th>
                    <th className="px-5 py-4">Gross yield</th>
                  </tr>
                </thead>
                <tbody>
                  {PRICE_ROWS.map((r, i) => (
                    <tr
                      key={i}
                      className="border-t border-border/50 hover:bg-secondary/60 transition-colors"
                      data-testid={`report-price-row-${i}`}
                    >
                      <td className="px-5 py-4 font-headings text-foreground">{r.area}</td>
                      <td className="px-5 py-4 text-foreground">{r.median}</td>
                      <td className="px-5 py-4"><YoYPill value={r.yoy} /></td>
                      <td className="px-5 py-4 text-foreground/70">{r.stock}</td>
                      <td className="px-5 py-4 text-primary">{r.yield}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 font-body text-xs text-foreground/50">
              Sources: Notaires de France (Q1 2026 index), Velmira Living portfolio (weighted, 60+ managed units).
            </p>
          </section>
        </Reveal>

        {/* STR TABLE */}
        <Reveal>
          <section id="short-term-rentals" className="mt-20 scroll-mt-24">
            <p className={overline}>
              <Percent size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              Short-term rental performance
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Occupancy, ADR and RevPAR, 2026
            </h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-3xl">
              Trailing 12-month averages from the Velmira Living managed
              portfolio — professionally operated, mairie-registered, LMNP-declared units.
              &ldquo;Y-round&rdquo; indicates a substantially stable occupancy curve outside of peak-summer.
            </p>
            <div className="mt-8 border border-border/60 bg-secondary/40 p-5 md:p-7">
              <StrChart rows={STR_ROWS} />
            </div>
            <div className="mt-6 border border-border/60 overflow-x-auto">
              <table data-testid="report-str-table" className="w-full text-left font-body text-sm">
                <thead className="bg-secondary">
                  <tr className="font-accent text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    <th className="px-5 py-4">Area</th>
                    <th className="px-5 py-4">Occupancy</th>
                    <th className="px-5 py-4">ADR</th>
                    <th className="px-5 py-4">RevPAR</th>
                    <th className="px-5 py-4">Season profile</th>
                  </tr>
                </thead>
                <tbody>
                  {STR_ROWS.map((r, i) => (
                    <tr
                      key={i}
                      className="border-t border-border/50 hover:bg-secondary/60 transition-colors"
                      data-testid={`report-str-row-${i}`}
                    >
                      <td className="px-5 py-4 font-headings text-foreground">{r.area}</td>
                      <td className="px-5 py-4 text-primary">{r.occ}</td>
                      <td className="px-5 py-4 text-foreground">{r.adr}</td>
                      <td className="px-5 py-4 text-foreground">{r.revpar}</td>
                      <td className="px-5 py-4 text-foreground/70">{r.months}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </Reveal>

        {/* BUYER MIX */}
        <Reveal>
          <section id="foreign-buyers" className="mt-20 scroll-mt-24">
            <p className={overline}>
              <Globe2 size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
              Foreign buyer nationality mix
            </p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Who&rsquo;s buying on the Riviera in 2026
            </h2>
            <p className="mt-4 font-body font-light text-foreground/70 max-w-3xl">
              Share of foreign-national buyers in Alpes-Maritimes, 2025 full-year
              (most recent complete data). Foreign buyers accounted for 38% of
              total transactions in the département — the highest share of any
              French coastal region.
            </p>

            <div className="mt-8 border border-border/60 bg-secondary/40 p-6 md:p-8">
              <BuyerDonut rows={BUYER_MIX} />
            </div>

            <ul
              data-testid="report-buyer-mix"
              className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              {BUYER_MIX.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-4 border border-border/60 bg-secondary p-5"
                  data-testid={`report-buyer-row-${i}`}
                >
                  <span className="text-2xl leading-none" aria-hidden="true">{b.flag}</span>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-headings text-lg text-foreground">{b.country}</p>
                      <div className="flex items-center gap-2">
                        <TrendBadge trend={b.trend} />
                        <span className="font-headings text-xl text-primary">{b.share}</span>
                      </div>
                    </div>
                    <p className="mt-1 font-body font-light text-xs text-foreground/60 leading-relaxed">
                      {b.note}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        {/* TAX */}
        <Reveal>
          <section id="tax" className="mt-20 scroll-mt-24">
            <p className={overline}>Taxation snapshot</p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              What foreign buyers should budget for
            </h2>
            <div className="mt-8 border border-border/60 bg-secondary/40 p-5 md:p-7">
              <TaxRangeChart rows={TAX_ROWS} />
            </div>
            <div className="mt-6 border border-border/60 overflow-x-auto">
              <table data-testid="report-tax-table" className="w-full text-left font-body text-sm">
                <thead className="bg-secondary">
                  <tr className="font-accent text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    <th className="px-5 py-4">Item</th>
                    <th className="px-5 py-4">Typical range</th>
                    <th className="px-5 py-4">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {TAX_ROWS.map((r, i) => (
                    <tr
                      key={i}
                      className="border-t border-border/50 hover:bg-secondary/60 transition-colors"
                      data-testid={`report-tax-row-${i}`}
                    >
                      <td className="px-5 py-4 font-headings text-foreground">{r.label}</td>
                      <td className="px-5 py-4 text-primary">{r.value}</td>
                      <td className="px-5 py-4 text-foreground/70">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 font-body text-xs text-foreground/50">
              Not tax advice — figures reflect typical ranges observed in 2026
              transactions. Always consult a French notaire and fiscaliste
              before purchase.
            </p>
          </section>
        </Reveal>

        {/* KEY FINDINGS */}
        <Reveal>
          <section id="findings" className="mt-20 scroll-mt-24">
            <p className={overline}>Key findings</p>
            <h2 className="mt-4 font-headings text-3xl sm:text-4xl text-foreground">
              Five things to know before buying
            </h2>
            <ol className="mt-8 space-y-6 font-body font-light text-foreground/85 leading-relaxed">
              {[
                { h: "Nice is the rational Riviera entry point.",
                  b: "Median €6,800/m² buys a professionally rentable Old Town or Musiciens 1-bed with a realistic 5%+ gross yield — a combination no other coastal European capital offers in 2026." },
                { h: "The €520K median hides a big premium curve.",
                  b: "Mont Boron and Villefranche trade at a 40–55% premium to the Nice median; Cap Ferrat trades 3.3× the median. Choose neighborhood first, m² second." },
                { h: "American demand is structural, not cyclical.",
                  b: "The +22% US share is driven by post-COVID lifestyle relocations and direct JFK-NCE flights (JetBlue, Delta) — not a short-term FX play. Expect sustained pressure on Nice prime through 2028." },
                { h: "Short-term rental yield is a compliance business.",
                  b: "Only mairie-registered, LMNP-declared, professionally managed units are hitting 5%+ net. Unregistered listings are being removed at scale — factor a boutique operator into the underwrite." },
                { h: "Tax friction is manageable — timing matters.",
                  b: "Notaire fees (7–8%) and secondary-home surcharges (60% in Nice) are the two costs foreign buyers most often underestimate. LMNP structuring can offset 60–90% of rental tax." },
              ].map((f, i) => (
                <li
                  key={i}
                  data-testid={`report-finding-${i}`}
                  className="border border-border/60 bg-secondary p-6"
                >
                  <p className="font-headings text-xl text-foreground leading-tight">
                    {String(i + 1).padStart(2, "0")}. {f.h}
                  </p>
                  <p className="mt-3">{f.b}</p>
                </li>
              ))}
            </ol>
          </section>
        </Reveal>

        {/* METHODOLOGY */}
        <Reveal>
          <section id="methodology" className="mt-20 scroll-mt-24 border-t border-border/60 pt-10">
            <p className={overline}>Methodology & sources</p>
            <div className="mt-6 space-y-4 font-body font-light text-foreground/75 leading-relaxed max-w-3xl">
              <p>
                Price data is drawn from the <em>Notaires de France</em> Q1 2026 index
                for the Alpes-Maritimes département, cross-checked against completed
                transactions in Velmira Living&rsquo;s advisory pipeline. Yields are computed
                from actual gross rental revenue on our managed portfolio of 60+
                units, divided by observed acquisition prices.
              </p>
              <p>
                Short-term rental data (occupancy, ADR, RevPAR) is drawn exclusively
                from the Velmira Living managed portfolio — professionally operated,
                mairie-registered, LMNP-declared units. Third-party AirDNA / STR
                aggregator estimates are typically 15–25% below observed portfolio
                performance for compliant operators, and are not used here.
              </p>
              <p>
                Foreign-buyer nationality mix reflects a proprietary sampling of
                notaire-confirmed transactions in Alpes-Maritimes for calendar year
                2025, weighted to reflect the département&rsquo;s 38% foreign-buyer share.
              </p>
              <p className="text-foreground/60">
                <strong className="text-foreground/80">License:</strong> This report
                is published under a Creative Commons BY 4.0 license. Journalists,
                researchers and analysts are welcome to quote, chart or excerpt any
                figure with attribution to Velmira Living and a link back to this page.
              </p>
            </div>
          </section>
        </Reveal>

        {/* CITE */}
        <Reveal>
          <section
            id="cite"
            data-testid="report-cite"
            className="mt-16 border border-primary/30 bg-secondary p-8 md:p-10"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className={overline}>
                  <Quote size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
                  Cite this report
                </p>
                <p className="mt-3 font-body font-light text-foreground/85 leading-relaxed">
                  {citation}
                </p>
              </div>
              <button
                type="button"
                onClick={copyCite}
                data-testid="report-copy-citation"
                className="inline-flex items-center gap-2 border border-primary hover:bg-primary hover:text-primary-foreground text-primary px-5 py-3 uppercase tracking-[0.2em] text-[10px] font-body transition-colors"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? "Copied" : "Copy citation"}
              </button>
            </div>
          </section>
        </Reveal>

        {/* CTA */}
        <Reveal>
          <div
            data-testid="report-cta"
            className="mt-14 border border-border/60 bg-card p-10 md:p-14 text-center"
          >
            <FileText size={28} className="text-primary mx-auto" strokeWidth={1.4} />
            <h2 className="mt-6 font-headings text-3xl sm:text-4xl leading-tight text-foreground">
              Need a custom underwrite for a specific property?
            </h2>
            <p className="mt-4 font-body font-light text-foreground/75 max-w-xl mx-auto">
              We publish this data publicly. We also run bespoke acquisition
              underwrites for buyers considering a specific address, building
              or neighborhood.
            </p>
            <Link
              to="/invest"
              onClick={() => track("buyer_cta_click", { cta_location: "data_report", lead_type: "buyer" })}
              data-testid="report-cta-button"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all"
            >
              Request a private underwrite <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
